import fs from 'node:fs';
import path from 'node:path';
import {
  createWalkYieldState,
  shouldSkipWorkspaceWalkEntry,
} from '../files/index/walk-workspace-tree.js';
import {
  fileBrowserReadInFlight,
  yieldForBridgeInteractivePriority,
  yieldUntilFileBrowserReadsIdle,
} from '../files/browser/in-flight.js';
import { isCliImmediateShutdownRequested } from '../runtime/cli-process-interrupt.js';
import { CliSqliteInterrupted } from '../sqlite/cli-database.js';
import { INDEX_WORK_YIELD_EVERY } from '../files/index/constants.js';
import { GitignoreFilter } from './gitignore-filter.js';
import { isCodeNavSupportedPath } from './language-registry.js';

type WalkYieldState = ReturnType<typeof createWalkYieldState>;

export { createWalkYieldState };

async function walkCodeNavFilesInDirectory(
  dir: string,
  baseDir: string,
  relDir: string,
  filter: GitignoreFilter,
  onFile: (relativePath: string) => void | Promise<void>,
  state: WalkYieldState,
): Promise<void> {
  await filter.enterDirectory(dir, relDir);

  let names: string[];
  try {
    names = await fs.promises.readdir(dir);
  } catch {
    filter.leaveDirectory(relDir);
    return;
  }

  for (const name of names) {
    if (shouldSkipWorkspaceWalkEntry(name)) continue;
    if (state.n > 0 && state.n % INDEX_WORK_YIELD_EVERY === 0) {
      if (isCliImmediateShutdownRequested()) throw new CliSqliteInterrupted();
      await yieldForBridgeInteractivePriority();
      if (isCliImmediateShutdownRequested()) throw new CliSqliteInterrupted();
    }
    if (fileBrowserReadInFlight()) {
      await yieldUntilFileBrowserReadsIdle();
    }
    state.n++;

    const relPath = relDir ? `${relDir}/${name}` : name;
    const full = path.join(dir, name);
    let stat: fs.Stats;
    try {
      stat = await fs.promises.stat(full);
    } catch {
      continue;
    }

    if (stat.isDirectory()) {
      if (filter.isIgnored(relPath, { directory: true })) continue;
      await walkCodeNavFilesInDirectory(full, baseDir, relPath, filter, onFile, state);
      continue;
    }

    if (!stat.isFile()) continue;
    if (filter.isIgnored(relPath)) continue;
    if (!isCodeNavSupportedPath(relPath)) continue;
    await Promise.resolve(onFile(relPath));
  }

  filter.leaveDirectory(relDir);
}

export async function walkCodeNavFilesAsync(
  rootDir: string,
  onFile: (relativePath: string) => void | Promise<void>,
  state: WalkYieldState = createWalkYieldState(),
): Promise<void> {
  const resolved = path.resolve(rootDir);
  const filter = new GitignoreFilter();
  await walkCodeNavFilesInDirectory(resolved, resolved, '', filter, onFile, state);
}
